import React from 'react';
import { motion } from 'framer-motion';
import { Search, Lightbulb, Rocket, TrendingUp } from 'lucide-react';
import { revealVariants, staggerContainer } from '../../utils/motion';
import { useInViewOnce } from '../../hooks/useInViewOnce';

const steps = [
  {
    step: "01",
    title: "Discover",
    subtitle: "Understanding client needs",
    description: "We conduct comprehensive analysis to understand your unique challenges, objectives, and operational requirements.",
    icon: Search,
    color: "from-blue-500 to-indigo-600"
  },
  {
    step: "02",
    title: "Design", 
    subtitle: "Building tailored strategies",
    description: "Our experts craft customized solutions and strategic roadmaps aligned with your business goals and industry best practices.",
    icon: Lightbulb,
    color: "from-purple-500 to-pink-600"
  },
  {
    step: "03",
    title: "Deliver",
    subtitle: "Executing with precision", 
    description: "We implement solutions with meticulous attention to detail, ensuring quality deliverables and milestone achievements.",
    icon: Rocket,
    color: "from-orange-500 to-red-600"
  },
  {
    step: "04",
    title: "Develop",
    subtitle: "Long-term growth & sustainability",
    description: "We provide ongoing support and optimization to ensure sustainable growth and continuous improvement.",
    icon: TrendingUp,
    color: "from-green-500 to-teal-600"
  }
];

export const HowWeWorkSection = () => {
  const [ref, isInView] = useInViewOnce();

  return (
    <section className="py-20 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-[#1F3C88]/5 to-[#FF5C8A]/5 rounded-full transform -translate-x-36 -translate-y-36" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#FF8C42]/5 to-[#1F3C88]/5 rounded-full transform translate-x-48 translate-y-48" />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={revealVariants}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A]/10 to-[#FF8C42]/10 text-[#1F3C88] px-4 py-2 rounded-full text-sm font-medium mb-6"
          >
            <span>Our Process</span>
          </motion.div>
          
          <motion.h2 
            variants={revealVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            How We <span className="bg-gradient-to-r from-[#1F3C88] to-[#FF5C8A] bg-clip-text text-transparent">Work</span>
          </motion.h2>
          
          <motion.p 
            variants={revealVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Our proven 4-step methodology ensures consistent results, exceptional quality, 
            and sustainable long-term success for every project.
          </motion.p>
        </motion.div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block relative">
          {/* Animated Connection Line */}
          <motion.div
            className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#1F3C88] via-[#FF5C8A] to-[#FF8C42] transform -translate-y-1/2 rounded-full"
            initial={{ scaleX: 0, originX: 0 }}
            animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 2, delay: 0.5 }}
          />

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-4 gap-8"
          >
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={step.step}
                  variants={revealVariants}
                  custom={index}
                  className="relative group"
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 text-center relative z-10 border border-gray-100 group-hover:border-[#FF5C8A]/30">
                    {/* Step Number Badge */}
                    <motion.div
                      className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-20"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      {step.step}
                    </motion.div>
                    
                    {/* Icon */}
                    <motion.div
                      className="w-16 h-16 bg-gradient-to-br from-[#1F3C88]/10 to-[#FF5C8A]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 mt-8 group-hover:scale-110 transition-transform duration-300"
                      whileHover={{ 
                        background: "linear-gradient(135deg, #1F3C88, #FF5C8A)",
                      }}
                      onHoverStart={() => {}}
                    >
                      <IconComponent 
                        size={32} 
                        className="text-[#1F3C88] group-hover:text-white transition-colors duration-300" 
                      />
                    </motion.div>
                    
                    {/* Content */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-[#1F3C88] transition-colors">
                      {step.title}
                    </h3>
                    
                    <h4 className="text-[#FF5C8A] font-semibold mb-4 text-sm uppercase tracking-wide">
                      {step.subtitle}
                    </h4>
                    
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {step.description}
                    </p>

                    {/* Hover Glow Effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-[#FF5C8A]/5 to-[#FF8C42]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                    />
                  </div>

                  {/* Connection Node */}
                  {index < steps.length - 1 && (
                    <motion.div
                      className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-30"
                      initial={{ scale: 0 }}
                      animate={isInView ? { scale: 1 } : { scale: 0 }}
                      transition={{ duration: 0.5, delay: 1 + index * 0.2 }}
                    >
                      <div className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-[#FF5C8A]">
                        <motion.div 
                          className="w-3 h-3 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full"
                          animate={{ scale: [1, 1.5, 1] }}
                          transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                        />
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>

        {/* Mobile Stack */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="lg:hidden space-y-8"
        >
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <motion.div
                key={step.step}
                variants={revealVariants}
                custom={index}
                className="relative group"
              >
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 flex items-start space-x-4">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] rounded-full flex items-center justify-center text-white font-bold mb-4">
                      {step.step}
                    </div>
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1F3C88]/10 to-[#FF5C8A]/10 rounded-xl flex items-center justify-center">
                      <IconComponent size={24} className="text-[#1F3C88]" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-grow">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {step.title}
                    </h3>
                    <h4 className="text-[#FF5C8A] font-semibold mb-3 text-sm">
                      {step.subtitle}
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-sm">
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Connection Line for Mobile */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center py-4">
                    <motion.div
                      className="w-0.5 h-8 bg-gradient-to-b from-[#FF5C8A] to-[#FF8C42] rounded-full"
                      initial={{ scaleY: 0 }}
                      animate={isInView ? { scaleY: 1 } : { scaleY: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 + index * 0.2 }}
                    />
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          variants={revealVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-[#1F3C88]/5 to-[#FF5C8A]/5 rounded-2xl p-8 backdrop-blur-sm border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Experience our proven methodology
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Let's discuss how our 4-step approach can deliver exceptional results for your next project.
            </p>
            <motion.a
              href="/request-service"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#FF5C8A] to-[#FF8C42] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg transition-shadow"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Start Your Project</span>
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};